import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {map, Observable, of, switchMap, zip} from 'rxjs';
import {Insurer, InsurerBranches, InsurerContact, InsurerContactConfiguration} from './insurer';
import {EnrolmentBlobStorageLocation} from '../../../prudent-plus-shared/services/blob-storage/enrolment-blob-storage';
import {EnrolmentBlobStorageService} from '../../../prudent-plus-shared/services/blob-storage/enrolment-blob-storage.service';
import {ENVIRONMENT} from 'src/environments/environment.token';

@Injectable({
  providedIn: 'root',
})
export class InsurerService {
  private http = inject(HttpClient);
  private blobStorageService = inject(EnrolmentBlobStorageService);
  private environment = inject(ENVIRONMENT);
  static NO_INSURER_ID: number = -1;
  static DEFAULT_INSURER_COLOR = '#0c5df8';
  static NO_BRANCH_ID: number = -1;

  private insurers: Insurer[] = [];
  private saibaInsurers: Insurer[] = [];
  private insurersById: Map<number, Insurer> = new Map<number, Insurer>();

  getInsurer(insurerId: number): Insurer {
    if (insurerId === InsurerService.NO_INSURER_ID) {
      return new Insurer(
        insurerId,
        'All Insurers',
        null,
        InsurerService.DEFAULT_INSURER_COLOR,
        false,
        false,
        true,
        '',
        [],
        '',
        'All Insurers',
        ''
      );
    }

    return this.insurersById.get(insurerId)!;
  }

  getSaibaInsurer(saibaInsurerId: number, isLI?: boolean): Insurer | undefined {
    return this.saibaInsurers.find((i) => i.id === saibaInsurerId && i.isLi === isLI);
  }

  getInsurers(): Insurer[] {
    return this.insurers;
  }

  getSaibaInsurers(): Insurer[] {
    return this.saibaInsurers;
  }

  mapInsurer(insurerId: number, saibaInsurerId: number | null, isLi: boolean): Observable<boolean> {
    let insurer = this.getInsurer(insurerId);

    insurer.saibaId = saibaInsurerId;
    insurer.isLi = isLi;

    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer`, insurer.toJSON()).pipe(
      switchMap(() => {
        this.insurers = [];

        return this.loadInsurers(true);
      })
    );
  }

  importInsurer(saibaInsurer: Insurer) {
    let insurerCopy = new Insurer(
      InsurerService.NO_INSURER_ID,
      saibaInsurer.name,
      saibaInsurer.id,
      InsurerService.DEFAULT_INSURER_COLOR,
      saibaInsurer.isLi,
      saibaInsurer.includeLastDate,
      saibaInsurer.isActive,
      '',
      saibaInsurer.branches,
      '',
      saibaInsurer.name,
      '',
      saibaInsurer.tpaId
    );

    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer`, insurerCopy.toJSON()).pipe(
      switchMap(() => {
        this.insurers = [];

        return zip(this.loadInsurers(true), this.loadSaibaInsurers());
      })
    );
  }

  updateInsurer(
    insurer: Insurer,
    insurerLogoFile?: File
  ): Observable<any> {
    let update = () => {
      return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer`, insurer.toJSON()).pipe(
        switchMap(() => {
          this.insurers = [];

          return zip(this.loadInsurers(true), this.loadSaibaInsurers());
        })
      );
    };

    if (insurerLogoFile) {
      return this.blobStorageService.uploadBlob(insurer.id,
        insurerLogoFile,
        insurerLogoFile.name,
        EnrolmentBlobStorageLocation.INSURER_LOGO)
        .pipe(
          switchMap((fileUrl) => {
            insurer.logo = fileUrl;

            return update();
          })
        );
    } else {
      return update();
    }
  }

  loadInsurers(includeInactive: boolean): Observable<boolean> {
    let includeInactiveValue = includeInactive ? 1 : 0;

    return this.http.get<any[]>(`${this.environment.appConfig.apiBaseUrl}/insurers`, {
      params: {
        include_inactive: includeInactiveValue,
      },
    }).pipe(map((insurers) => {
      this.insurers = insurers.map((obj: any) => Insurer.fromJSON(obj))
        .sort((i1, i2) => i1.name.localeCompare(i2.name));
      this.insurersById.clear();
      this.insurers.forEach((i) => this.insurersById.set(i.id, i));

      return true;
    }));
  }
  getBranch(includeInactive: boolean, branchId: number): Observable<InsurerBranches> {
    let includeInactiveValue = includeInactive ? 1 : 0;

    return this.http.get<InsurerBranches>(`${this.environment.appConfig.apiBaseUrl}/insurers`, {
      params: {
        include_inactive: includeInactiveValue,
        branch_id: branchId,
      },
    });
  }

  loadSaibaInsurers(): Observable<boolean> {
    if (this.saibaInsurers.length > 0) return of(true);

    return this.http.get<any[]>(`${this.environment.appConfig.apiBaseUrl}/saiba-insurers`).pipe(map((insurers) => {
      this.saibaInsurers = insurers.map((obj: any) => Insurer.fromJSON(obj))
        .sort((i1, i2) => i1.name.localeCompare(i2.name));

      return true;
    }));
  }

  addInsurerBranch(insurerBranch: InsurerBranches) {
    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer-branch-master`, insurerBranch);
  }

  addInsurerContact(insurerContact: InsurerContact) {
    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer/contacts`, insurerContact);
  }

  deleteInsurerContact(contactId: number) {
    return this.http.delete(`${this.environment.appConfig.apiBaseUrl}/insurer/contacts`, {
      params: {
        contact_id: contactId,
      },
    });
  }

  insurerContactConfiguration(insurerContactConfiguration: InsurerContactConfiguration) {
    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/insurer-contact-policy-type-join`, insurerContactConfiguration);
  }

  getInsurerContactConfiguration(policyTypeId: number): Observable<Insurer[]> {
    return this.http.get<Insurer[]>(`${this.environment.appConfig.apiBaseUrl}/insurer/contacts`, {
      params: {
        policy_type_id: policyTypeId,
      },
    });
  }

  fetchIntegrationKeys():Observable<string[]> {
    return this.http.get<string[]>(`${this.environment.appConfig.apiBaseUrl}/insurer-integration-keys`);
  }
}
